import React, { useMemo } from 'react';
import LoadingIndicator from '../../components/shared/LoadingIndicator/LoadingIndicator';
import AnalysisReport from './AnalysisReport';
import { getCompetitorsGeneralScoreData, getGeneralScoreData } from './utils';
import { getFlatSections } from '../utils';
import useLiveAnalysisWithSaving from '../hooks/useLiveAnalysisWithSaving';
import useLiveSectionFetching from '../hooks/useLiveSectionFetching';


export default function LiveAnalysisReport() {
    useLiveSectionFetching();

    const {
        analysisStructure,
        lastAnalysisGuid,
        lastAnalysisCreatedAt,
        lastAnalysisUrl,
        lastAnalysisSections,
        lastAnalysisCompetitors,
        sectionResults,
        competitorSectionResults,
    } = useLiveAnalysisWithSaving();

    const filteredSectionResults = Object.keys(sectionResults).reduce((acc, section) => {
        if (sectionResults[section] !== null) {
            return { ...acc, [section]: sectionResults[section] };
        } else {
            return acc;
        }
    }, {});

    const filteredCompetitorResults = Object.keys(competitorSectionResults).reduce((accSections, section) => {
        return {
            ...accSections,
            [section]: Object.keys(competitorSectionResults[section]).reduce((accCompetitors, competitor) => {
                if (competitorSectionResults[section][competitor] !== null) {
                    return { ...accCompetitors, [competitor]: competitorSectionResults[section][competitor] };
                } else {
                    return accCompetitors;
                }
            }, {}),
        };
    }, {});

    const selectedSections = useMemo(
        () => (analysisStructure ? getFlatSections(analysisStructure).filter((section) => lastAnalysisSections.includes(section.key)) : []),
        [analysisStructure, lastAnalysisSections]
    );

    const hasAnalysisParameters = lastAnalysisGuid && lastAnalysisUrl;
    const showReport = hasAnalysisParameters && analysisStructure;

    const analysisProgress = lastAnalysisSections.length > 0 ? Math.floor((Object.keys(sectionResults).length * 100) / lastAnalysisSections.length) : 0;

    const isFullReport = useMemo(
        () => !!analysisStructure && Object.values(analysisStructure).flat().length === lastAnalysisSections.length,
        [analysisStructure, lastAnalysisSections.length]
    );

    const competitorUrls = useMemo(() => lastAnalysisCompetitors.map((competitor) => competitor.websiteUrl), [lastAnalysisCompetitors]);

    return (
        <>
            {!hasAnalysisParameters && (
                <LoadingIndicator
                    detailed
                    text="Loading results"
                />
            )}

            {showReport && (
                <AnalysisReport
                    guid={lastAnalysisGuid}
                    url={lastAnalysisUrl}
                    structure={analysisStructure}
                    scoreData={getGeneralScoreData(selectedSections, sectionResults)}
                    competitorsScoreData={getCompetitorsGeneralScoreData(competitorUrls, selectedSections, competitorSectionResults)}
                    totalSectionsCount={selectedSections.filter((section) => sectionResults[section.key] !== null).length}
                    sectionResults={filteredSectionResults}
                    competitorSectionResults={filteredCompetitorResults}
                    createdAt={lastAnalysisCreatedAt || ''}
                    isFullReport={isFullReport}
                    analysisProgress={analysisProgress}
                />
            )}
        </>
    );
}
